import {
  addDashboard,
  coerceDashboardsPrefs,
  coerceHomeDashboard,
  dashboardsKey,
  defaultDashboard,
  deleteDashboard,
  homeDashboardKey,
  homeDashboardValue,
  isDefaultDashboardsPrefs,
  renameDashboard,
  restoreStarter,
  saveDashboardWidgets,
  setDefaultDashboard,
  type WidgetPlacement,
} from '@/lib/dashboardLayouts'
import { DASHBOARD_WIDGETS, isWidgetAllowed } from '@/lib/dashboardWidgets'
import { navPermissionsFor, usePermission } from '@/lib/permissions'
import { useTenant } from '@/providers/TenantProvider'
import { useStoredPreference } from './useStoredPreference'

function newDashboardId(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
}

/**
 * The stored Home value in the SAME shape it is written in, so a value read
 * back is exactly the value saved (it once was not, and Home reverted on every
 * refresh). `widgets: null` is the default set, which is deleted, not stored.
 */
type StoredHome = { widgets: WidgetPlacement[] | null }
const coerceStoredHome = (raw: unknown): StoredHome => ({ widgets: coerceHomeDashboard(raw) })
const isDefaultStoredHome = (value: StoredHome) => value.widgets === null

/**
 * The widgets on the signed-in user's Home, in the selected tenant. `widgets`
 * is null for the default set (D7 decides what that is); `reset` returns to it.
 */
export function useHomeDashboard() {
  const { selectedTenantId } = useTenant()
  const key = selectedTenantId ? homeDashboardKey(selectedTenantId) : null
  const { value, update, isLoading, ready, saveFailed } = useStoredPreference(key, coerceStoredHome, isDefaultStoredHome)
  return {
    widgets: value.widgets,
    isLoading,
    ready,
    saveFailed,
    save: (widgets: WidgetPlacement[]) => update(homeDashboardValue(widgets)),
    reset: () => update({ widgets: null }),
  }
}

/**
 * The signed-in user's named dashboards in the selected tenant (the Dashboards
 * page). Until they change anything this is the three starter dashboards,
 * which are not stored, so they keep getting the latest version of them.
 */
export function useDashboards() {
  const { selectedTenantId } = useTenant()
  const key = selectedTenantId ? dashboardsKey(selectedTenantId) : null
  const { value: prefs, update, isLoading, ready, saveFailed } = useStoredPreference(
    key,
    coerceDashboardsPrefs,
    isDefaultDashboardsPrefs,
  )
  return {
    prefs,
    isLoading,
    ready,
    saveFailed,
    defaultDashboard: defaultDashboard(prefs),
    setDefault: (id: string) => update(setDefaultDashboard(prefs, id)),
    /** Adds a dashboard and returns its id, so the page can open it. */
    create: (name: string, widgets: WidgetPlacement[] = []) => {
      const id = newDashboardId()
      update(addDashboard(prefs, { id, name: name.trim(), widgets }))
      return id
    },
    rename: (id: string, name: string) => update(renameDashboard(prefs, id, name)),
    saveWidgets: (id: string, widgets: WidgetPlacement[]) =>
      update(saveDashboardWidgets(prefs, id, widgets)),
    restore: (id: string) => update(restoreStarter(prefs, id)),
    remove: (id: string) => update(deleteDashboard(prefs, id)),
  }
}

/**
 * Which widgets the signed-in user may see in the selected tenant. Permission
 * HIDES: a widget left out here is never rendered, even from a saved layout.
 */
export function useAllowedWidgets() {
  const { canAny } = usePermission()
  // Not memoised: `canAny` is a new function every render, and the catalog is
  // a few dozen entries.
  const allowed = DASHBOARD_WIDGETS.filter((w) => isWidgetAllowed(w, { canAny, navPermissionsFor }))
  const allowedIds = new Set(allowed.map((w) => w.id))
  return { allowed, isAllowed: (id: string) => allowedIds.has(id) }
}
